import { GridStackNode, GridStackWidget } from "gridstack";

export enum WidgetType {
  GENERIC = "generic",
  WEATHER = "weather",
  DELIVERIES_BY_CATEGORY = "deliveries_by_category",
  INSTALLS = "installs",
  MOBILE_LOCATION_ALLOWED = "mobile_location_allowed",
  REGIONS_SNAPSHOT = "regions_snapshot",
  ON_PROPERTY = "on_property",
  DELIVERED_CONTENT = "delivered_content",
  ASSISTANT_THREADS = "assistant_threads",
  ASSISTANT_DEVICES_USERS = "assistant_devices_users",
  ASSISTANT_SHARES_RATINGS_FEEDBACK = "assistant_shares_ratings_feedback",
}

export interface WeatherOptions {
  type?: string;
  zipCode?: string;
}

export interface WidgetsDTO {
  h?: number;
  w?: number;
  x?: number;
  y?: number;
  type: WidgetType;
  assistantType?: "tripist" | "explorist" | "aura";
  weatherOptions?: WeatherOptions;
}

export abstract class Widget {
  constructor(
    private _type: WidgetType,
    private _title: string,
    private _id: number,
    private _widgetDTO: WidgetsDTO,
    private _multiple: boolean,
    public hovering = false,
  ) {}

  //Unique identifier for Gridstack widget initialization
  get code() {
    return `${this.type}_${this._id}`;
  }

  get type() {
    return this._type;
  }

  get title() {
    return this._title;
  }

  get width() {
    return this._gridStackWidgetOptions?.w;
  }

  get height() {
    return this._gridStackWidgetOptions?.h;
  }

  get xPosition() {
    return this._gridStackWidgetOptions?.x;
  }

  get yPosition() {
    return this._gridStackWidgetOptions?.y;
  }

  get widgetDTO() {
    return this._widgetDTO;
  }

  get multiple() {
    return this._multiple;
  }

  get _gridStackWidgetOptions(): GridStackWidget {
    return {
      h: this.widgetDTO.h,
      w: this.widgetDTO.w,
      y: this.widgetDTO.y,
      x: this.widgetDTO.x,
    };
  }

  updateWidgetValues(updatedGridItem: GridStackNode) {
    this.widgetDTO.h = updatedGridItem.h;
    this.widgetDTO.w = updatedGridItem.w;
    this.widgetDTO.x = updatedGridItem.x;
    this.widgetDTO.y = updatedGridItem.y;
  }

  resizeWidget(width: number, height: number) {
    this.widgetDTO.w = width;
    this.widgetDTO.h = height;
  }

  toWidgetDTO(): WidgetsDTO {
    return {
      h: this.height,
      w: this.width,
      x: this.xPosition,
      y: this.yPosition,
      type: this.type,
      assistantType: this.widgetDTO.assistantType,
    };
  }
}

export class GenericWidget extends Widget {
  constructor(id: number, widgetDTO: WidgetsDTO) {
    super(WidgetType.GENERIC, "Generic", id, widgetDTO, false);
  }
}

export class WeatherWidget extends Widget {
  constructor(id: number, widgetDTO: WidgetsDTO) {
    super(WidgetType.WEATHER, "Weather", id, widgetDTO, true);
  }

  get weatherOptions() {
    return this.widgetDTO.weatherOptions;
  }

  updateWeatherOptions(options: WeatherOptions) {
    this.widgetDTO.weatherOptions = options;
  }
}

export class DeliveriesByCategoryWidget extends Widget {
  constructor(id: number, widgetDTO: WidgetsDTO) {
    super(
      WidgetType.DELIVERIES_BY_CATEGORY,
      "Deliveries by category",
      id,
      widgetDTO,
      false,
    );
  }
}

export class InstallsWidget extends Widget {
  constructor(id: number, widgetDTO: WidgetsDTO) {
    super(WidgetType.INSTALLS, "Installs", id, widgetDTO, false);
  }
}

export class MobileLocationAllowedWidget extends Widget {
  constructor(id: number, widgetDTO: WidgetsDTO) {
    super(
      WidgetType.MOBILE_LOCATION_ALLOWED,
      "Mobile location permissions",
      id,
      widgetDTO,
      false,
    );
  }
}

export class RegionsSnapshotWidget extends Widget {
  constructor(id: number, widgetDTO: WidgetsDTO) {
    super(WidgetType.REGIONS_SNAPSHOT, "Snapshot", id, widgetDTO, false);
  }
}

export class OnPropertyWidget extends Widget {
  constructor(id: number, widgetDTO: WidgetsDTO) {
    super(WidgetType.ON_PROPERTY, "On Property", id, widgetDTO, false);
  }
}

export class DeliveredContentWidget extends Widget {
  constructor(id: number, widgetDTO: WidgetsDTO) {
    super(
      WidgetType.DELIVERED_CONTENT,
      "Delivered Content",
      id,
      widgetDTO,
      false,
    );
  }
}

export class AssistantThreadsWidget extends Widget {
  constructor(id: number, widgetDTO: WidgetsDTO) {
    super(
      WidgetType.ASSISTANT_THREADS,
      "Assistant Threads",
      id,
      widgetDTO,
      true,
    );
  }
}

export class AssistantDevicesUsersWidget extends Widget {
  constructor(id: number, widgetDTO: WidgetsDTO) {
    super(
      WidgetType.ASSISTANT_DEVICES_USERS,
      "Assistant Devices/Users",
      id,
      widgetDTO,
      true,
    );
  }
}

export class AssistantSharesRatingsFeedbackWidget extends Widget {
  constructor(id: number, widgetDTO: WidgetsDTO) {
    super(
      WidgetType.ASSISTANT_SHARES_RATINGS_FEEDBACK,
      "Assistant Shares/Ratings/Feedback",
      id,
      widgetDTO,
      true,
    );
  }
}
